import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { z } from "zod";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PostCard } from "@/components/PostCard";
import { posts } from "@/data/posts";
import { siteConfig } from "@/config";

const searchSchema = z.object({ q: z.string().optional() });

export const Route = createFileRoute("/search")({
  validateSearch: searchSchema,
  head: () => {
    const title = `Search — ${siteConfig.siteName}`;
    const desc = `Search articles on ${siteConfig.siteName}: CRM reviews, comparisons, and buyer's guides.`;
    const url = `${siteConfig.domain}/search`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "robots", content: "noindex,follow" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: SearchPage,
  errorComponent: ({ error, reset }) => {
    console.error(error);
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-3xl px-4 py-16 text-center">
          <h1 className="text-xl font-semibold">Search failed to load.</h1>
          <button onClick={() => reset()} className="mt-6 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
            Try again
          </button>
        </main>
        <Footer />
      </div>
    );
  },
});

function SearchPage() {
  const { q } = Route.useSearch();
  const navigate = useNavigate();
  const [value, setValue] = useState(q ?? "");

  useEffect(() => {
    setValue(q ?? "");
  }, [q]);

  const results = useMemo(() => {
    const needle = (q ?? "").trim().toLowerCase();
    if (!needle) return [];
    return posts.filter((p) => {
      const hay = [
        p.title,
        p.description,
        p.category,
        ...p.keywords,
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [q]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="text-3xl font-bold tracking-tight">Search</h1>
        <form
          className="mt-6 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ to: "/search", search: { q: value } });
          }}
        >
          <input
            type="search"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Search articles…"
            className="w-full rounded-md border border-input bg-background px-4 py-2 text-base focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <button
            type="submit"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          >
            Search
          </button>
        </form>

        {q ? (
          <>
            <p className="mt-6 text-sm text-muted-foreground">
              {results.length} result{results.length === 1 ? "" : "s"} for “{q}”
            </p>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {results.map((p) => (
                <PostCard key={p.id} post={p} />
              ))}
            </div>
          </>
        ) : (
          <p className="mt-6 text-sm text-muted-foreground">
            Try “HubSpot”, “Pipedrive”, or “buyer's guide”.
          </p>
        )}
      </main>
      <Footer />
    </div>
  );
}
